import Divider from '@/components/common/Divider';
import Modal, { ModalHeader, ModalTab, ModalTabList, ModalTitle } from '@/components/modal/Modal';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import {
    closeNotifications,
    markAllAsSeen,
    markAsSeen,
    Notification,
    openNotifications,
    selectNotificationsHideTillNextLogin,
    selectNotificationsOpened,
    selectNotificationsVisited,
    setHideNotificationsTillNextLogin,
    setNotificationsVisited,
    toggleNotifications
} from '@/state/reducers/notificationSlice';
import { selectNotificationSettings } from '@/state/reducers/settingSlice';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import useNotifications from '@/utils/hooks/useNotifications';
import { parseNotificationSettings } from '@/utils/hooks/useToast';
import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { MdCheck } from 'react-icons/md';
import { NotificationItem } from './NotificationItem';

interface NotificationsModalProps {}

const NotificationsModal = (props: NotificationsModalProps) => {
    const dispatch = useAppDispatch();

    const opened = useAppSelector(selectNotificationsOpened);
    const credentials = useAppSelector(selectCredentials);
    const notificationVisited = useAppSelector(selectNotificationsVisited);
    const notificationSettings = useAppSelector(selectNotificationSettings);
    const hideNotificationsTillNextLogin = useAppSelector(selectNotificationsHideTillNextLogin);

    const lang = useLanguage();
    const { notifications, unseenNotifications, seenNotifications, allCount, seenCount, unseenCount } =
        useNotifications({});

    const defaultConfig = parseNotificationSettings(notificationSettings);

    const [showNotificationsQuantity, setShowNotificationsQuantity] = useState(10);
    const [isChecked, setIsChecked] = useState(hideNotificationsTillNextLogin);
    const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());

    // Track which notifications have been viewed
    const handleNotificationViewed = useCallback((id: string) => {
        setViewedIds((prev) => new Set([...Array.from(prev), id]));
    }, []);

    const handleClose = useCallback(() => {
        // Mark all viewed notifications as seen when closing
        viewedIds.forEach((id) => dispatch(markAsSeen(id)));
        dispatch(closeNotifications());
        // setViewedIds(new Set()); // Reset viewed IDs
    }, [viewedIds, dispatch]);

    const handlers: any = useMemo(
        () => ({
            open: () => dispatch(openNotifications()),
            close: () => handleClose(),
            toggle: () => dispatch(toggleNotifications())
        }),
        [opened, handleClose]
    );

    useDidUpdate(() => {
        if (!notificationVisited && notifications.length > 0 && defaultConfig.show && !hideNotificationsTillNextLogin) {
            handlers.open();
        }
    }, [notifications]);

    return (
        <Modal opened={opened} handlers={handlers} className="lg:!h-[670px]" size="max-w-xl">
            <ModalHeader>
                <ModalTitle>
                    {lang.settings.notificationsTab}
                    {hideNotificationsTillNextLogin && (
                        <div className={cn('text-xs font-normal text-brand-neutral-400')}>
                            <span>{lang.notifications.popUpsDisabledUntilNextLogin}</span>
                        </div>
                    )}
                </ModalTitle>
                <div className="flex flex-col items-end">
                    {unseenCount > 0 && (
                        <button
                            type="button"
                            className="text-xs text-brand-primary hover:text-brand-primary-light flex items-center gap-2"
                            onClick={() => dispatch(markAllAsSeen())}>
                            <MdCheck className="w-4 h-4" />
                            {lang.notifications.markAllAsSeen}
                        </button>
                    )}
                </div>
            </ModalHeader>
            <Divider neutral />
            <Tab.Group defaultIndex={1}>
                <ModalTabList>
                    <ModalTab>
                        {lang.notifications.all} {allCount > 0 && `(${allCount})`}
                    </ModalTab>
                    <ModalTab>
                        {lang.notifications.unseen} {unseenCount > 0 && `(${unseenCount})`}
                    </ModalTab>
                    <ModalTab>
                        {lang.notifications.seen} {seenCount > 0 && `(${seenCount})`}
                    </ModalTab>
                </ModalTabList>
                <Tab.Panels className="flex flex-col flex-1 basis-0 overflow-y-auto">
                    <Tab.Panel className="flex-1 basis-0">
                        <FilteredNotifications
                            notifications={notifications}
                            showNotificationsQuantity={showNotificationsQuantity}
                            setShowNotificationsQuantity={setShowNotificationsQuantity}
                            onViewed={handleNotificationViewed}
                            viewedIds={viewedIds}
                        />
                    </Tab.Panel>
                    <Tab.Panel className="flex-1 basis-0">
                        <FilteredNotifications
                            notifications={unseenNotifications}
                            showNotificationsQuantity={showNotificationsQuantity}
                            setShowNotificationsQuantity={setShowNotificationsQuantity}
                            onViewed={handleNotificationViewed}
                            viewedIds={viewedIds}
                        />
                    </Tab.Panel>
                    <Tab.Panel className="flex-1 basis-0">
                        <FilteredNotifications
                            notifications={seenNotifications}
                            showNotificationsQuantity={showNotificationsQuantity}
                            setShowNotificationsQuantity={setShowNotificationsQuantity}
                            onViewed={handleNotificationViewed}
                            viewedIds={viewedIds}
                        />
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
            <Divider neutral />
            <div
                className={cn('text-brand-text flex flex-wrap-reverse items-center p-2 sm:p-4 text-sm gap-2', {
                    ['justify-between']: !hideNotificationsTillNextLogin,
                    ['justify-end']: hideNotificationsTillNextLogin
                })}>
                {!hideNotificationsTillNextLogin && (
                    <div className="flex items-start gap-2">
                        <input
                            id="hide-notifications-checkbox"
                            type="checkbox"
                            style={{
                                height: 12,
                                width: 12
                            }}
                            className={cn(
                                'cursor-pointer mt-0.5 checked:accent-brand-primary-dark bg-brand-neutral-400'
                            )}
                            checked={isChecked}
                            onChange={() => setIsChecked(!isChecked)}
                        />
                        <label
                            htmlFor="hide-notifications-checkbox"
                            className={cn('text-xs cursor-pointer', {
                                ['text-brand-primary-light']: isChecked,
                                ['text-brand-text']: !isChecked
                            })}>
                            <span>{lang.notifications.hideNotificationPopUpsUntilNextLogin}</span>
                            <br />
                            <span className="text-2xs text-brand-neutral-400">
                                {lang.notifications.notificationsAreStillAvailable}
                            </span>
                        </label>
                    </div>
                )}
                <div className="flex gap-2">
                    <button
                        className={cn('rounded-md p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500', {
                            ['w-full']: !credentials
                        })}
                        onClick={() => {
                            handlers.close();
                            dispatch(setNotificationsVisited(true));
                            if (isChecked) dispatch(setHideNotificationsTillNextLogin(true));
                            else dispatch(setHideNotificationsTillNextLogin(false));
                        }}>
                        {lang.notifications.dismiss}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default NotificationsModal;

interface FilteredNotificationsProps {
    notifications: Notification[];
    showNotificationsQuantity: number;
    setShowNotificationsQuantity: (value: number) => void;
    onViewed: (id: string) => void;
    viewedIds: Set<string>;
}

export const FilteredNotifications = ({
    notifications,
    showNotificationsQuantity,
    setShowNotificationsQuantity,
    onViewed,
    viewedIds
}: FilteredNotificationsProps) => {
    const lang = useLanguage();
    return (
        <Fragment>
            {notifications.length === 0 ? (
                <div className="flex h-full items-center justify-center text-brand-neutral-500">
                    {lang.notifications.noNotifications}
                </div>
            ) : (
                <div className="flex-1 basis-0 h-full overflow-y-scroll w-full bg-brand-background-dark text-brand-text">
                    {notifications.slice(0, showNotificationsQuantity).map((notification, id) => (
                        <NotificationItem
                            key={id}
                            notification={notification}
                            viewed={viewedIds.has(notification.id)}
                            onViewed={onViewed}
                        />
                    ))}
                    {notifications.length > showNotificationsQuantity && (
                        <div className="flex justify-center p-2 py-4 sm:px-4">
                            <div className="text-brand-neutral-400 hover:text-brand-primary-light cursor-pointer text-2xs xs:text-xs sm:text-sm text-center">
                                <div onClick={() => setShowNotificationsQuantity(showNotificationsQuantity + 10)}>
                                    {lang.notifications.showMore}
                                </div>
                            </div>
                        </div>
                    )}
                    {notifications.length < showNotificationsQuantity && (
                        <div className="text-brand-neutral-400 text-2xs xs:text-xs sm:text-sm text-center p-2 py-4 sm:px-4">
                            <div>{lang.notifications.showingAllNotifications}</div>
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
};
